import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { AuthBllService } from "../../../../features/auth/auth-bll.service";
import { CommonModule } from "@angular/common";
import { DeviceTypeService } from "../../../../core/services/device-type.service";
import { HomeBllService } from "../../../../features/home/home-bll.service";
import { LanguageService } from "../../../../core/services/language.service";
import { Subscription } from "rxjs";
import { filter } from 'rxjs/operators';

declare global {
  interface Window {
    googleTranslateElementInit: () => void;
    google: any;
  }
}

/**
 * @description
 * The `HeaderComponent` is responsible for displaying the application's header bar.
 * It includes the client's name, profile picture, navigation links, and logout functionality.
 * Additionally, it integrates the Google Translate widget for language translation.
 */
@Component({
  selector: "app-header",
  imports: [CommonModule],
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  /** The display name of the current client/user */
  public clientName: string | null = null;

  /** URL or Base64 data for the client’s profile picture */
  public profilePic: string | null = null;

  /** Indicates if the current device is a mobile device */
  isMobileDevice: boolean = false;

  /** Indicates if the user is authenticated */
  isAuthenticated: boolean = false;

  /** Indicates if the current page is the home page */
  isHomePage: boolean = false;
  isPromotionPage: boolean = false;

  /** The currently selected language */
  currentLang: string = "en";

  promotionCurrentLang: string = "";

  /** Observer to monitor changes in the Google Translate widget */
  private googleTranslateObserver: MutationObserver | null = null;

  /** Subscription to monitor router events */
  private routerSub!: Subscription;

  /** Indicates if the Google Translate script has been loaded */
  private scriptLoaded = false;
  hideButton = false;
  private routerSubscription!: Subscription;
  isOpen = false;
  constructor(
    private homeBll: HomeBllService,
    private authService: AuthBllService,
    private deviceService: DeviceTypeService,
    private router: Router,
    private renderer: Renderer2,
    private languageService: LanguageService
  ) {}

  /**
   * @description Angular lifecycle hook - called once the component is initialized.
   * Sets up device type detection, authentication state monitoring, and language change observation.
   */
  ngOnInit(): void {
    this.isMobileDevice = this.deviceService.isMobile(); // Check if the device is mobile
    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      console.log("isAuthenticated:", isAuthenticated);
      this.isAuthenticated = isAuthenticated;
    });

    this.isHomePage = this.homeBll.isHomePage();
    this.isPromotionPage = this.homeBll.isPromotionPage();
    this.observeLanguageChange();

    // Check initial route
    this.checkRoute(this.router.url);
    this.setDefaultLanguage(this.router.url);

    // Subscribe to future route changes
    this.routerSubscription = this.router.events
    .pipe(filter(event => event instanceof NavigationEnd))
    .subscribe((event) => this.checkRoute((event as NavigationEnd).urlAfterRedirects));
    
  }

  setDefaultLanguage(url: string): void {
    if (url === '/promotion-marathi') {
      this.promotionCurrentLang = 'mr';
    } else if (url === '/promotion-english') {
      this.promotionCurrentLang = 'en';
    } else {
      this.promotionCurrentLang = '';
    }
  }

  private checkRoute(url: string): void {
   this.hideButton = url === '/promotion-marathi' || url === '/promotion-english';
  }

  /**
   * @description Angular lifecycle hook - called after the view is initialized.
   * Loads the Google Translate script and initializes the translation widget.
   */
  ngAfterViewInit() {
    this.loadGoogleTranslateScript();

    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        setTimeout(() => this.initTranslateWidget(), 500);
      }
    });
  }

  /**
   * @description Loads the Google Translate script dynamically.
   * Ensures the script is loaded only once and initializes the translation widget.
   */
  private loadGoogleTranslateScript(): void {
    if (this.scriptLoaded) {
      this.initTranslateWidget();
      return;
    }

    window.googleTranslateElementInit = () => {
      this.initTranslateWidget();
      this.scriptLoaded = true;
    };

    const script = document.createElement("script");
    script.src =
      "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    script.defer = true;

    document.body.appendChild(script);
  }

  /**
   * @description Initializes the Google Translate widget.
   * Prevents duplicate rendering and sets up the translation options.
   */
  private initTranslateWidget(): void {
    const el = document.getElementById("google_translate_element");

    // Prevent duplicate rendering
    if (!el || el.innerHTML.trim() !== "") return;

    if (window.google?.translate) {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: "en,hi,mr",
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        "google_translate_element"
      );
      this.preventGoogleTranslateNavigation();
    }
  }

  /**
   * @description Handles logout operation.
   * Saves the current state and navigates to the login page.
   */
  onLogin(): void {
    const currentRoute = this.router.url; // get current active route

    // check route condition
    const state = currentRoute.includes('/promotion-marathi')
      ? '/promotion-marathi'
      : '/home';
      
    this.authService.saveStorageValue("state", state);
    this.router.navigate(["/auth/init"]);
  }

  /**
   * @description Disables form submission and navigation in the Google Translate widget.
   */
  private preventGoogleTranslateNavigation(): void {
    const GOOGLE_TRANSLATE_INIT_DELAY = 1500; // ms
    this.googleTranslateObserver = new MutationObserver(() => {
      const anchors = document.querySelectorAll(
        '#google_translate_element a[href="#"]'
      );
      anchors.forEach((anchor) => {
        (anchor as HTMLElement).removeAttribute("href");
      });
    });

    // Start observing DOM changes in the translate element
    const target = document.getElementById("google_translate_element");
    if (target) {
      this.googleTranslateObserver.observe(target, {
        childList: true,
        subtree: true,
      });
    }

    this.pollForGoogleTranslateAnchors(
      '#google_translate_element a[href="#"]',
      100,
      3000
    );
  }

  /**
   * @description Polls for the existence of Google Translate anchors and removes their href attribute.
   * @param selector The CSS selector for the anchors.
   * @param intervalMs Polling interval in milliseconds.
   * @param maxWaitMs Maximum wait time in milliseconds.
   */
  private pollForGoogleTranslateAnchors(
    selector: string,
    intervalMs: number,
    maxWaitMs: number
  ): void {
    const start = Date.now();
    const poll = () => {
      const anchors = document.querySelectorAll(selector);
      if (anchors.length > 0) {
        anchors.forEach((anchor) => {
          (anchor as HTMLElement).removeAttribute("href");
        });
        return;
      }
      if (Date.now() - start < maxWaitMs) {
        setTimeout(poll, intervalMs);
      }
    };
    poll();
  }

  /**
   * @description Angular lifecycle hook - called when the component is destroyed.
   * Cleans up subscriptions and observers.
   */
  ngOnDestroy(): void {
    this.routerSub.unsubscribe();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.googleTranslateObserver) {
      this.googleTranslateObserver.disconnect();
      this.googleTranslateObserver = null;
    }
  }

  /**
   * @description Detects when the user changes language in the Google Translate dropdown.
   */
  private observeLanguageChange(): void {
    const currentLang = this.languageService.getSelectedLanguageFromCookie();
    this.handleLanguageChange(currentLang);
  }

  /**
   * @description Handles app-specific actions when the language changes.
   * Updates the current language and refreshes localized data.
   * @param lang The new language code.
   */
  private handleLanguageChange(lang: string): void {
    console.log("Language switched to:", lang);
    this.currentLang = lang;
    this.languageService.setLanguage(lang);
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.isOpen) return; 
    const target = event.target as HTMLElement;

    if (!target.closest('.dropdown')) {
      this.isOpen = false;
    }
  }

  switchLanguage(lang: string) {
    if (lang === 'mr') {
      this.promotionCurrentLang = 'mr';
      this.router.navigate(['/promotion-marathi']);
    } else if (lang === 'en') {
      this.promotionCurrentLang = 'en';
      this.router.navigate(['/promotion-english']);
    }
    this.isOpen = false;
  }
}
